/**
 * SurfaceExpandedContent - Surface product expanded content
 *
 * Redesigned step-based UX flow:
 * 1. Upload mode selector (friendly cards: "Individual Tiles" vs "Ready-Made Texture")
 * 2. Image upload area + settings (grout, pattern options) side by side
 * 3. Prominent Generate button with "needs regeneration" indicator
 * 4. Texture preview with cropper for thumbnail
 * 5. Variant links
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Cropper from 'react-easy-crop';
import {
  CircleAlert,
  CircleCheck,
  FlipHorizontal,
  ImageIcon,
  Loader2,
  RefreshCw,
  Shuffle,
  Sparkles,
  ZoomIn,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import { PlankUploader } from '../../shared/media/PlankUploader';
import { CabochonUploader } from '../../shared/media/CabochonUploader';
import { OpusUploader } from '../../shared/media/OpusUploader';
import { isCabochonPattern, isUnbalancedCabochonList } from '../../../utils/cabochonImages';
import { isOpusImageCountValid, isOpusPattern } from '../../../utils/opusImages';
import { plankCm } from '../../../../../../common/utils/units';
import { BuildFromPhotosDialog, patternLabel, type BuildFromPhotosApply } from '../../shared/media/BuildFromPhotosDialog';
import { useBuildFromPhotos } from '../../shared/media/build-from-photos.store';
import { PreviewInRoomButton } from '../../shared/media/PreviewInRoomButton';
import { VariantLinksSection } from './VariantLinksSection';
import type { SurfaceExpandedContentProps } from './types';

/** Toggle pill for the stackbond generation options — same shape for randomize and mirror. */
const OptionToggle: React.FC<{
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onToggle: () => void;
}> = ({ active, icon, label, onToggle }) => (
  <button
    type="button"
    onClick={(e) => { e.stopPropagation(); onToggle(); }}
    className={cn(
      'flex select-none items-center gap-1.5 border px-3 py-1.5 text-xs transition-colors',
      active
        ? 'border-foreground/30 bg-muted font-medium text-foreground'
        : 'border-border text-muted-foreground hover:border-foreground/20 hover:bg-muted/50'
    )}
  >
    {icon}
    {label}
  </button>
);

/** Numeric field with a trailing unit — used by both texture dimensions and grout size. */
const UnitInput: React.FC<{
  id?: string;
  value: number | string;
  unit: string;
  placeholder?: string;
  required?: boolean;
  invalid?: boolean;
  className?: string;
  min?: number;
  step?: number;
  onChange: (raw: string) => void;
}> = ({ id, value, unit, placeholder, required, invalid, className, min, step, onChange }) => (
  <div className={cn('relative', className)}>
    <Input
      id={id}
      type="number"
      required={required}
      aria-invalid={invalid}
      value={value}
      min={min}
      step={step}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onClick={(e) => e.stopPropagation()}
      className="pe-7 text-center"
    />
    <span className="pointer-events-none absolute end-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
      {unit}
    </span>
  </div>
);

export const SurfaceExpandedContent: React.FC<SurfaceExpandedContentProps> = ({
  variant,
  uploadMode,
  plankGroupId,
  groupImages,
  loadingPlankGroups,
  onPlankImagesChange,
  onUploadModeChange,
  productPhotoUrls,
  isGenerating,
  onGenerateMaterial,
  randomize,
  setRandomize,
  mirror,
  setMirror,
  texturePreviewUrl,
  currentCrop,
  currentZoom,
  onVariantCropChange,
  onVariantZoomChange,
  setVariantCrops,
  setVariantZooms,
  setVariantCroppedAreas,
  variantCropsRef,
  variantZoomsRef,
  cropperInteractedRef,
  settingThumbnail,
  autoSaveThumbnail,
  onVariantGroutSizeChange,
  onVariantGroutColorChange,
  onSeamlessWidthChange,
  onSeamlessHeightChange,
  onVariantThumbnailChange,
  onVariantLinkChange,
  onVariantAddToCartLinkChange,
}) => {
  const { t } = useTranslation();

  // Normalize pattern name: "stack bond" -> "stackbond"
  const isStackbondPattern =
    typeof variant.axes['pattern'] === 'string' &&
    variant.axes['pattern'].toLowerCase().replace(/\s+/g, '') === 'stackbond';

  // Cabochon takes octagons and corner squares in two uploaders, the same number of each
  const isCabochon = isCabochonPattern(variant.axes['pattern']);
  // Opus takes one image per piece (or one for all); its size is the smallest square
  const isOpus = isOpusPattern(variant.axes['pattern']);
  const sizeAxis = variant.axes['size'] as { width?: number; height?: number; unit?: string } | undefined;
  const opusModuleCm = isOpus && sizeAxis?.width && sizeAxis?.height
    ? Math.min(plankCm(sizeAxis.width, sizeAxis.unit), plankCm(sizeAxis.height, sizeAxis.unit))
    : undefined;

  // Determine if regeneration is needed
  const hasImages = groupImages.length > 0;
  const hasTexture = !!texturePreviewUrl;

  // Track user-initiated changes since last generation to show "needs regeneration"
  const [dirtySinceGenerate, setDirtySinceGenerate] = useState(false);
  const trackingEnabledRef = useRef(false);
  const prevImagesCountRef = useRef<number | null>(null);
  const prevGroutWidthRef = useRef<number | undefined>(undefined);
  const prevGroutColorRef = useRef<string | undefined>(undefined);
  const prevRandomizeRef = useRef<boolean | undefined>(undefined);
  const prevMirrorRef = useRef<boolean | undefined>(undefined);

  // Delay tracking by 1.5s after mount to let all async hydration settle
  useEffect(() => {
    const timer = setTimeout(() => {
      // Snapshot current values as baseline, then enable tracking
      prevImagesCountRef.current = groupImages.length;
      prevGroutWidthRef.current = variant.visualization.surface?.grout?.width;
      prevGroutColorRef.current = variant.visualization.surface?.grout?.color;
      prevRandomizeRef.current = randomize;
      prevMirrorRef.current = mirror;
      trackingEnabledRef.current = true;
    }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Detect user-initiated changes after stabilization
  useEffect(() => {
    if (!trackingEnabledRef.current) return;

    const imagesChanged = groupImages.length !== prevImagesCountRef.current;
    const groutWidthChanged = variant.visualization.surface?.grout?.width !== prevGroutWidthRef.current;
    const groutColorChanged = variant.visualization.surface?.grout?.color !== prevGroutColorRef.current;
    const randomizeChanged = randomize !== prevRandomizeRef.current;
    const mirrorChanged = mirror !== prevMirrorRef.current;

    if (imagesChanged || groutWidthChanged || groutColorChanged || randomizeChanged || mirrorChanged) {
      setDirtySinceGenerate(true);
    }

    prevImagesCountRef.current = groupImages.length;
    prevGroutWidthRef.current = variant.visualization.surface?.grout?.width;
    prevGroutColorRef.current = variant.visualization.surface?.grout?.color;
    prevRandomizeRef.current = randomize;
    prevMirrorRef.current = mirror;
  }, [groupImages.length, variant.visualization.surface?.grout?.width, variant.visualization.surface?.grout?.color, randomize, mirror]);

  // Reset dirty flag when generation starts
  useEffect(() => {
    if (isGenerating) {
      setDirtySinceGenerate(false);
    }
  }, [isGenerating]);

  // Build from photos: its result is tile images (for the generator) or a
  // ready-made seamless texture, and the variant switches to the mode it needs.
  const [photosOpen, setPhotosOpen] = useState(false);
  // The run lives outside the dialog, so the button can say it is still working once the dialog is closed.
  const photoRun = useBuildFromPhotos((s) => s.runs[variant.uuid]);
  // Photos offered by "Add product with AI": the dialog opens on them, ticked.
  const photoOffer = useBuildFromPhotos((s) => s.offers[variant.uuid]);
  const takePhotoOffer = useBuildFromPhotos((s) => s.takeOffer);
  const [suggestedPhotos, setSuggestedPhotos] = useState<string[] | undefined>();
  useEffect(() => {
    if (!photoOffer) return;
    setSuggestedPhotos(takePhotoOffer(variant.uuid));
    setPhotosOpen(true);
  }, [photoOffer, takePhotoOffer, variant.uuid]);
  const applyPhotos = ({ mode, files, cellSizeCm, mirror: laidMirrored }: BuildFromPhotosApply) => {
    const wanted = mode === 'tiles' ? 'multiple' : 'single';
    let key = plankGroupId;
    if (wanted !== uploadMode) {
      onUploadModeChange?.(wanted);
      key = wanted === 'single' ? variant.uuid : (variant.visualization.surface?.plankGroupId || variant.uuid);
    }
    if (mode === 'tiles') {
      onPlankImagesChange?.(key, files);
      // The photos lay the tiles mirrored (a cement tile's rosettes): the texture should too.
      if (laidMirrored && isStackbondPattern) setMirror(true);
      return;
    }
    onPlankImagesChange?.(key, files.slice(0, 1));
    if (files[0] && onVariantThumbnailChange) {
      const reader = new FileReader();
      reader.onload = () => onVariantThumbnailChange(variant.uuid, reader.result as string);
      reader.readAsDataURL(files[0]);
    }
    if (cellSizeCm?.length === 2 && cellSizeCm[0] > 0 && cellSizeCm[1] > 0) {
      onSeamlessWidthChange?.(variant.uuid, +(cellSizeCm[0] / 100).toFixed(4));
      onSeamlessHeightChange?.(variant.uuid, +(cellSizeCm[1] / 100).toFixed(4));
    }
  };

  const needsGeneration = uploadMode === 'multiple' && hasImages && dirtySinceGenerate && !isGenerating;
  const canGenerate = uploadMode === 'multiple' && hasImages && onGenerateMaterial;
  const cabochonBlocked = isCabochon && isUnbalancedCabochonList(groupImages);
  const opusBlocked = isOpus && groupImages.length > 0 && !isOpusImageCountValid(groupImages.length);

  return (
    <div className="flex flex-col gap-5">
      {/* ===== ROW 1: Upload Area + Settings Side by Side ===== */}
      <div className={cn('flex gap-6', uploadMode === 'single' ? 'items-center' : 'items-start')}>

        {/* LEFT: Image Upload Area */}
        <div className="min-w-0 flex-[2]">
          {uploadMode === 'single' ? (
            // Single mode: texture with dimension lines on edges
            (texturePreviewUrl && groupImages.length === 0) || groupImages.length > 0 ? (
              <div className="mt-3 flex min-h-[280px] w-full items-center justify-center">
                <div className="inline-flex flex-row items-stretch">
                  {/* Image + bottom dimension line */}
                  <div className="flex flex-col">
                    {/* The texture image */}
                    <div className="relative size-[240px] overflow-hidden border border-border bg-muted">
                      <img
                        src={groupImages.length > 0 ? URL.createObjectURL(groupImages[0]) : texturePreviewUrl}
                        alt="Texture"
                        className="block size-full object-cover"
                      />
                      <label className="absolute bottom-2 end-2 cursor-pointer bg-foreground/60 px-3 py-1 text-xs text-background transition-colors hover:bg-foreground/80">
                        {t('PAGES.NEW_PRODUCT.V2.VARIANT_TABLE.REPLACE')}
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              onPlankImagesChange?.(plankGroupId, [file]);
                              if (onVariantThumbnailChange) {
                                const reader = new FileReader();
                                reader.onload = () => {
                                  onVariantThumbnailChange(variant.uuid, reader.result as string);
                                };
                                reader.readAsDataURL(file);
                              }
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </label>
                    </div>

                    {/* Bottom dimension arrow (Width) */}
                    <div
                      className="mt-1.5 flex w-[240px] flex-col items-center text-muted-foreground"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg width="240" height="8" viewBox="0 0 240 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <line x1="6" y1="4" x2="234" y2="4" stroke="currentColor" strokeWidth="0.75" />
                        <path d="M0 4L6 1.5V6.5L0 4Z" fill="currentColor" />
                        <path d="M240 4L234 1.5V6.5L240 4Z" fill="currentColor" />
                      </svg>
                      <UnitInput
                        className="mt-1 w-[110px]"
                        required
                        invalid={!variant.visualization.surface?.seamlessParams?.textureWidth}
                        value={variant.visualization.surface?.seamlessParams?.textureWidth || ''}
                        min={0.1}
                        step={0.5}
                        unit="m"
                        placeholder={t('PAGES.NEW_PRODUCT.V3.SURFACE.TEXTURE_WIDTH')}
                        onChange={(raw) => {
                          const val = parseFloat(raw);
                          onSeamlessWidthChange?.(variant.uuid, isNaN(val) ? 0 : Math.max(0, val));
                        }}
                      />
                    </div>
                  </div>

                  {/* Right dimension arrow (Height) */}
                  <div
                    className="ms-1.5 flex h-[240px] flex-row items-center text-muted-foreground"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <svg width="8" height="240" viewBox="0 0 8 240" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <line x1="4" y1="6" x2="4" y2="234" stroke="currentColor" strokeWidth="0.75" />
                      <path d="M4 0L1.5 6H6.5L4 0Z" fill="currentColor" />
                      <path d="M4 240L1.5 234H6.5L4 240Z" fill="currentColor" />
                    </svg>
                    <UnitInput
                      className="ms-1 w-[90px]"
                      required
                      invalid={!variant.visualization.surface?.seamlessParams?.textureHeight}
                      value={variant.visualization.surface?.seamlessParams?.textureHeight || ''}
                      min={0.1}
                      step={0.5}
                      unit="m"
                      placeholder={t('PAGES.NEW_PRODUCT.V3.SURFACE.TEXTURE_HEIGHT')}
                      onChange={(raw) => {
                        const val = parseFloat(raw);
                        onSeamlessHeightChange?.(variant.uuid, isNaN(val) ? 0 : Math.max(0, val));
                      }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              // No texture yet — show uploader
              <PlankUploader
                plankGroupId={plankGroupId}
                files={groupImages}
                onChange={(files) => {
                  onPlankImagesChange?.(plankGroupId, files);
                  if (files.length > 0 && onVariantThumbnailChange) {
                    const reader = new FileReader();
                    reader.onload = () => {
                      onVariantThumbnailChange(variant.uuid, reader.result as string);
                    };
                    reader.readAsDataURL(files[0]);
                  }
                }}
                maxFiles={1}
                mode="single"
              />
            )
          ) : (
            // Multiple mode: plank uploader
            loadingPlankGroups.has(plankGroupId) ? (
              <div className="flex items-center justify-center gap-3 border border-dashed border-border bg-muted py-8 text-xs text-muted-foreground">
                <Loader2 className="size-5 animate-spin" />
                {t('PAGES.NEW_PRODUCT.V3.SURFACE.LOADING_IMAGES')}
              </div>
            ) : isOpus ? (
              <OpusUploader
                plankGroupId={plankGroupId}
                files={groupImages}
                onChange={(files) => onPlankImagesChange?.(plankGroupId, files)}
                moduleCm={opusModuleCm}
              />
            ) : isCabochon ? (
              <CabochonUploader
                plankGroupId={plankGroupId}
                files={groupImages}
                onChange={(files) => onPlankImagesChange?.(plankGroupId, files)}
              />
            ) : (
              <PlankUploader
                plankGroupId={plankGroupId}
                files={groupImages}
                onChange={(files) => onPlankImagesChange?.(plankGroupId, files)}
                mode="multiple"
              />
            )
          )}

          {onPlankImagesChange && (
            <div className="mt-3 flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
              <Button variant="outline" size="sm" onClick={() => setPhotosOpen(true)} aria-busy={photoRun?.phase === 'running'}>
                {photoRun?.phase === 'running' ? (
                  <Loader2 className="animate-spin" />
                ) : photoRun?.phase === 'result' ? (
                  <CircleCheck className="text-success" />
                ) : photoRun?.phase === 'error' ? (
                  <CircleAlert className="text-destructive" />
                ) : (
                  <Sparkles />
                )}
                {photoRun?.phase === 'running'
                  ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.BUTTON_RUNNING')
                  : photoRun?.phase === 'result'
                    ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.BUTTON_READY')
                    : t('PAGES.NEW_PRODUCT.V3.PHOTOS.OPEN')}
              </Button>
              <span className="text-xs text-muted-foreground">
                {photoRun?.phase === 'running'
                  ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.HINT_RUNNING')
                  : photoRun?.phase === 'result' && photoRun.result
                    ? (photoRun.result.mode === 'none'
                      ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.NOTHING_USABLE')
                      : t('PAGES.NEW_PRODUCT.V3.PHOTOS.HINT_READY', {
                        pattern: patternLabel(photoRun.result.pattern) || '—',
                        count: photoRun.result.images.length,
                      }))
                    : photoRun?.phase === 'error'
                      ? t('PAGES.NEW_PRODUCT.V3.PHOTOS.HINT_FAILED')
                      : t('PAGES.NEW_PRODUCT.V3.PHOTOS.OPEN_HINT')}
              </span>
            </div>
          )}
          <BuildFromPhotosDialog
            runKey={variant.uuid}
            open={photosOpen}
            onOpenChange={setPhotosOpen}
            size={variant.axes['size'] as any}
            pattern={typeof variant.axes['pattern'] === 'string' ? variant.axes['pattern'] : undefined}
            colorHex={(variant.axes['color'] as { hex?: string } | undefined)?.hex}
            productPhotoUrls={productPhotoUrls}
            suggestedUrls={suggestedPhotos}
            onApply={applyPhotos}
          />
        </div>

        {/* RIGHT: Settings + Generate + Preview */}
        <div className="flex min-w-[200px] flex-1 flex-col gap-4">

          {/* Grout Settings - multiple mode only */}
          {uploadMode === 'multiple' && (
            <div className="border border-border bg-muted/40 p-4">
              <p className="mb-3 text-xs font-medium text-muted-foreground">
                {t('PAGES.NEW_PRODUCT.V3.SURFACE.GROUT_SETTINGS')}
              </p>
              <div className="flex gap-3">
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor={`variant-${variant.uuid}-grout-size`} className="text-xs font-medium text-muted-foreground">
                    {t('PAGES.NEW_PRODUCT.V2.VARIANT_TABLE.GROUT_SIZE')}
                  </Label>
                  <UnitInput
                    id={`variant-${variant.uuid}-grout-size`}
                    value={variant.visualization.surface?.grout?.width || 0}
                    unit="mm"
                    onChange={(raw) => onVariantGroutSizeChange?.(variant.uuid, parseFloat(raw) || 0)}
                  />
                </div>
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor={`variant-${variant.uuid}-grout-color`} className="text-xs font-medium text-muted-foreground">
                    {t('PAGES.NEW_PRODUCT.V2.VARIANT_TABLE.GROUT_COLOR')}
                  </Label>
                  {(() => {
                    const rawColor = variant.visualization.surface?.grout?.color || '#FFFFFF';
                    const displayColor = rawColor.length >= 7 ? rawColor.substring(0, 7) : '#FFFFFF';
                    return (
                      <div className="flex items-center gap-1.5">
                        <input
                          type="color"
                          value={displayColor}
                          onChange={(e) => onVariantGroutColorChange?.(variant.uuid, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="size-8 shrink-0 cursor-pointer appearance-none border border-input bg-transparent p-0 [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:border-0"
                        />
                        <Input
                          id={`variant-${variant.uuid}-grout-color`}
                          value={displayColor.toUpperCase()}
                          onChange={(e) => onVariantGroutColorChange?.(variant.uuid, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="flex-1"
                        />
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Stackbond pattern options */}
              {isStackbondPattern && (
                <div className="mt-3 flex justify-center gap-2">
                  <OptionToggle
                    active={randomize}
                    icon={<Shuffle className="size-3.5 shrink-0" />}
                    label={t('PAGES.NEW_PRODUCT.V3.SURFACE.RANDOMIZE')}
                    onToggle={() => setRandomize(!randomize)}
                  />
                  <OptionToggle
                    active={mirror}
                    icon={<FlipHorizontal className="size-3.5 shrink-0" />}
                    label={t('PAGES.NEW_PRODUCT.V3.SURFACE.MIRROR')}
                    onToggle={() => setMirror(!mirror)}
                  />
                </div>
              )}
            </div>
          )}

          {/* ===== Generate Button + Status (multiple mode) ===== */}
          {canGenerate && (
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                size="lg"
                className={cn('w-full', needsGeneration && 'border-warning/40 text-warning hover:text-warning')}
                onClick={() => {
                  const pattern =
                    typeof variant.axes['pattern'] === 'string'
                      ? variant.axes['pattern'].toLowerCase().replace(/\s+/g, '')
                      : '';
                  const options = pattern === 'stackbond' ? { randomize, mirror } : undefined;
                  onGenerateMaterial(variant, plankGroupId, groupImages, options);
                }}
                disabled={isGenerating || cabochonBlocked || opusBlocked}
              >
                {isGenerating ? <Loader2 className="animate-spin" /> : <RefreshCw />}
                {isGenerating
                  ? t('PAGES.NEW_PRODUCT.V2.VARIANT_TABLE.GENERATING')
                  : hasTexture
                    ? t('PAGES.NEW_PRODUCT.V3.SURFACE.REGENERATE_TEXTURE')
                    : t('PAGES.NEW_PRODUCT.V3.SURFACE.GENERATE_TEXTURE')
                }
              </Button>

              {/* Status indicator */}
              {needsGeneration && (
                <div className="flex items-center gap-1.5 border border-warning/20 bg-warning/10 px-3 py-1.5 text-xs text-warning">
                  <CircleAlert className="size-4 shrink-0" />
                  {t('PAGES.NEW_PRODUCT.V3.SURFACE.NEEDS_REGENERATION')}
                </div>
              )}
              {hasTexture && !needsGeneration && !isGenerating && (
                <div className="flex items-center gap-1.5 border border-success/20 bg-success/10 px-3 py-1.5 text-xs text-success">
                  <CircleCheck className="size-4 shrink-0" />
                  {t('PAGES.NEW_PRODUCT.V3.SURFACE.TEXTURE_READY')}
                </div>
              )}
            </div>
          )}

          {/* ===== Texture Preview + Cropper ===== */}
          {texturePreviewUrl ? (
            <div>
              <div
                className="relative aspect-square w-full max-w-[180px] overflow-hidden border border-border bg-card rounded-xl"
                onClick={(e) => e.stopPropagation()}
              >
                <Cropper
                  key={texturePreviewUrl}
                  image={texturePreviewUrl}
                  crop={currentCrop}
                  zoom={currentZoom}
                  minZoom={1}
                  maxZoom={10}
                  aspect={1}
                  objectFit="contain"
                  showGrid={false}
                  onInteractionStart={() => {
                    cropperInteractedRef.current.add(variant.uuid);
                  }}
                  onCropChange={(newCrop) => {
                    if (cropperInteractedRef.current.has(variant.uuid)) {
                      setVariantCrops((prev) => ({ ...prev, [variant.uuid]: newCrop }));
                    }
                    variantCropsRef.current[variant.uuid] = newCrop;
                  }}
                  onZoomChange={(newZoom) => {
                    if (cropperInteractedRef.current.has(variant.uuid)) {
                      setVariantZooms((prev) => ({ ...prev, [variant.uuid]: newZoom }));
                    }
                    variantZoomsRef.current[variant.uuid] = newZoom;
                  }}
                  onCropComplete={(croppedArea, croppedAreaPixels) => {
                    setVariantCroppedAreas((prev) => ({ ...prev, [variant.uuid]: croppedAreaPixels }));
                    if (!cropperInteractedRef.current.has(variant.uuid)) {
                      return;
                    }
                    const latestCrop = variantCropsRef.current[variant.uuid] || currentCrop;
                    const latestZoom = variantZoomsRef.current[variant.uuid] ?? currentZoom;
                    if (onVariantCropChange) {
                      onVariantCropChange(variant.uuid, latestCrop);
                    }
                    if (onVariantZoomChange) {
                      onVariantZoomChange(variant.uuid, latestZoom);
                    }
                    autoSaveThumbnail(variant.uuid, texturePreviewUrl, croppedAreaPixels);
                  }}
                />
                {settingThumbnail.has(variant.uuid) && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-foreground/30 backdrop-blur-[2px]">
                    <Loader2 className="size-8 animate-spin text-background" />
                  </div>
                )}
              </div>
              {/* Zoom Slider */}
              <div
                className="mt-2.5 flex w-full max-w-[180px] items-center gap-2 border border-border bg-card rounded-xl px-3 py-1.5"
                onClick={(e) => e.stopPropagation()}
              >
                <ZoomIn className="size-4 shrink-0 text-muted-foreground" />
                <Slider
                  value={[currentZoom]}
                  min={1}
                  max={10}
                  step={0.1}
                  className="flex-1"
                  onValueChange={([newZoom]) => {
                    cropperInteractedRef.current.add(variant.uuid);
                    setVariantZooms((prev) => ({ ...prev, [variant.uuid]: newZoom }));
                  }}
                  onValueCommit={([newZoom]) => {
                    if (onVariantZoomChange) {
                      onVariantZoomChange(variant.uuid, newZoom);
                    }
                  }}
                />
                {/* Zoom level indicator */}
                <span className="w-7 shrink-0 select-none text-end text-xs font-medium text-muted-foreground">
                  {currentZoom.toFixed(1)}x
                </span>
              </div>
            </div>
          ) : null}

          {/* Try the texture in a room, saved or not */}
          <PreviewInRoomButton variant={variant} textureUrl={texturePreviewUrl} />

          {texturePreviewUrl ? null : uploadMode === 'multiple' ? (
            // No texture yet placeholder (multiple mode only)
            <div
              className="flex aspect-square w-full max-w-[180px] flex-col items-center justify-center gap-2 border border-dashed border-border bg-muted/30 text-muted-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <ImageIcon className="size-7 opacity-60" strokeWidth={1.5} />
              <p className="px-4 text-center text-xs">
                {t('PAGES.NEW_PRODUCT.V2.VARIANT_TABLE.NO_TEXTURE_YET')}
              </p>
            </div>
          ) : null}
        </div>
      </div>

      {/* ===== ROW 2: Variant Links ===== */}
      <VariantLinksSection
        variant={variant}
        onVariantLinkChange={onVariantLinkChange}
        onVariantAddToCartLinkChange={onVariantAddToCartLinkChange}
        layout="vertical"
      />
    </div>
  );
};

export default SurfaceExpandedContent;
